import { Outlet, useLocation } from "react-router-dom";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useAppViewportHeight } from "@/hooks/useAppViewportHeight";
import { Sidebar } from "@/components/layout/Sidebar";
import { BottomNav, BOTTOM_NAV_CLEARANCE } from "@/components/layout/BottomNav";
import { RouteFab } from "@/components/layout/RouteFab";
import { APP_CONTENT_WIDTH_CLASS } from "@/components/layout/pageLayout";
import { InstallPrompt, LastSyncedLabel } from "@/components/pwa";
import { OfflineOutboxBanner } from "@/components/shared/OfflineOutboxBanner";
import { OFFLINE_BANNER_MSG } from "@/lib/offline";
import { cn } from "@/lib/utils";

function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) return null;
  return (
    <div
      role="status"
      className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-center text-sm font-medium text-amber-950 dark:border-amber-800 dark:bg-amber-950/80 dark:text-amber-100"
      data-testid="offline-banner"
    >
      {OFFLINE_BANNER_MSG}
    </div>
  );
}

const contentColumnClass = cn(
  "mx-auto flex w-full min-w-0 flex-col overflow-x-clip px-4 py-4 sm:px-5 lg:px-6 lg:py-6",
  APP_CONTENT_WIDTH_CLASS
);

export function AppShell() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const location = useLocation();
  const lockChatLayout =
    location.pathname.startsWith("/projekt-ai") ||
    (location.pathname.startsWith("/asszisztens") && location.search.includes("mod=kerdezz"));
  useAppViewportHeight(!isDesktop);

  if (isDesktop) {
    return (
      <div
        className={cn(
          "bg-slate-50 pt-[env(safe-area-inset-top,0px)] text-slate-900 dark:bg-slate-950 dark:text-slate-50",
          lockChatLayout ? "flex h-dvh flex-col overflow-hidden" : "min-h-dvh"
        )}
      >
        <Sidebar />
        <div
          className={cn(
            "ml-[240px]",
            lockChatLayout ? "flex min-h-0 flex-1 flex-col" : "min-h-dvh"
          )}
        >
          <InstallPrompt />
          <OfflineBanner />
          <OfflineOutboxBanner />
          <main className={cn(lockChatLayout && "flex min-h-0 flex-1 flex-col")}>
            <div
              className={cn(contentColumnClass, lockChatLayout ? "min-h-0 flex-1" : "min-h-full")}
            >
              <div className={cn(lockChatLayout ? "flex min-h-0 flex-1 flex-col" : "flex-1")}>
                <Outlet />
              </div>
              {lockChatLayout ? null : (
                <LastSyncedLabel className="mt-8 shrink-0 text-center text-xs text-slate-500 dark:text-slate-400" />
              )}
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col overflow-hidden bg-slate-50 pt-[env(safe-area-inset-top,0px)] text-slate-900 dark:bg-slate-950 dark:text-slate-50"
      style={{
        height: "var(--app-shell-height, 100dvh)",
        minHeight: "var(--app-shell-height, 100dvh)"
      }}
      data-testid="app-shell-mobile"
    >
      <InstallPrompt />
      <OfflineBanner />
      <OfflineOutboxBanner />
      <main
        className={cn(
          "min-h-0 flex-1 overscroll-contain",
          lockChatLayout
            ? "flex flex-col overflow-hidden"
            : "overflow-y-auto [-webkit-overflow-scrolling:touch]"
        )}
      >
        <div
          className={cn(contentColumnClass, lockChatLayout ? "min-h-0 flex-1" : "min-h-full")}
          style={{ paddingBottom: BOTTOM_NAV_CLEARANCE }}
        >
          <div className={cn(lockChatLayout ? "flex min-h-0 flex-1 flex-col" : "flex-1")}>
            <Outlet />
          </div>
          {lockChatLayout ? null : (
            <LastSyncedLabel className="mt-6 shrink-0 text-center text-xs text-slate-500 dark:text-slate-400" />
          )}
        </div>
      </main>
      <RouteFab />
      <BottomNav />
    </div>
  );
}
